import en from './locales/en';
import es from './locales/es';
import {
  DEFAULT_LOCALE,
  LOCALES,
  type Locale,
  type MessageKey,
  type Messages,
} from './types';
import { MONTHS } from '../constants';

export {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_LABELS,
  LOCALE_NATIVE_NAMES,
  type Locale,
  type MessageKey,
  type Messages,
} from './types';

const catalogs: Record<Locale, Messages> = { en, es };

const STORAGE_KEY = 'calendarify-locale';

let currentLocale: Locale = DEFAULT_LOCALE;
let initialized = false;

function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value);
}

/** Extract locale prefix from a pathname (`/es/...` → `es`). */
export function getLocaleFromPath(pathname: string): Locale | null {
  const segment = pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  return isLocale(segment) && segment !== DEFAULT_LOCALE ? segment : null;
}

/** Remove a leading `/es` (or future locale) prefix from a path. */
export function stripLocalePrefix(pathname: string): string {
  const locale = getLocaleFromPath(pathname);
  if (!locale) {
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  }
  const rest = pathname.replace(/^\/+/, '').split('/').slice(1).join('/');
  return rest ? `/${rest}` : '/';
}

/** Prefix a site-absolute path with the locale when needed. */
export function localizePath(pathname: string, locale: Locale = currentLocale): string {
  const bare = stripLocalePrefix(pathname);
  if (locale === DEFAULT_LOCALE) {
    return bare === '/' ? '/' : bare;
  }
  if (bare === '/') {
    return `/${locale}/`;
  }
  return `/${locale}${bare.startsWith('/') ? bare : `/${bare}`}`;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  let text: string = catalogs[currentLocale][key] ?? catalogs[DEFAULT_LOCALE][key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{${name}}`).join(String(value));
    }
  }
  return text;
}

export function getMonthName(monthIndex: number): string {
  const slug = MONTHS[monthIndex];
  return t(`months.${slug}` as MessageKey);
}

export function getWeekdayLabels(startOnMonday: boolean): string[] {
  const sundayFirst = [
    t('weekdays.sun'),
    t('weekdays.mon'),
    t('weekdays.tue'),
    t('weekdays.wed'),
    t('weekdays.thu'),
    t('weekdays.fri'),
    t('weekdays.sat'),
  ];
  return startOnMonday ? [...sundayFirst.slice(1), sundayFirst[0]] : sundayFirst;
}

function detectLocale(): Locale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const fromPath = getLocaleFromPath(window.location.pathname);
  if (fromPath) {
    return fromPath;
  }

  // Default (unprefixed) English URLs intentionally stay English even if the
  // browser/storage prefer Spanish — switcher / /es/ URLs own that choice.
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored) && stored !== DEFAULT_LOCALE) {
      // Prefer URL as source of truth on English paths; only use storage when
      // we later add auto-redirect. For now path wins.
    }
  } catch {
    // ignore storage errors (private mode, etc.)
  }

  return DEFAULT_LOCALE;
}

/** Apply `lang` + hreflang alternates for the current page. */
export function applyDocumentLocale(): void {
  if (typeof document === 'undefined') return;

  document.documentElement.lang = currentLocale;

  const barePath = stripLocalePrefix(window.location.pathname);
  const origin = window.location.origin;

  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());

  const addAlternate = (hreflang: string, href: string) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    link.href = href;
    document.head.appendChild(link);
  };

  for (const locale of LOCALES) {
    addAlternate(locale, `${origin}${localizePath(barePath, locale)}`);
  }
  addAlternate('x-default', `${origin}${localizePath(barePath, DEFAULT_LOCALE)}`);
}

export function initI18n(): Locale {
  if (initialized) {
    return currentLocale;
  }
  currentLocale = detectLocale();
  try {
    localStorage.setItem(STORAGE_KEY, currentLocale);
  } catch {
    // ignore
  }
  applyDocumentLocale();
  initialized = true;
  return currentLocale;
}

/** Navigate to the same page in another locale (full navigation for SEO/crawlability). */
export function switchLocale(next: Locale): void {
  if (!isLocale(next)) return;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore
  }
  const nextPath = localizePath(window.location.pathname, next);
  const url = `${nextPath}${window.location.search}${window.location.hash}`;
  if (url === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    return;
  }
  window.location.assign(url);
}

/** Eager init when this module is imported in the browser. */
if (typeof window !== 'undefined') {
  initI18n();
}
